import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, CheckCircle2, AlertTriangle, BarChart3, Users, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';

function formatTTC(minutes) {
  if (!minutes) return '--';
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function BoardAnalytics() {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await api.get(`/boards/${boardId}/analytics`);
        setData(res.data);
      } catch (e) {
        console.error('Failed to load analytics:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [boardId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8 text-center text-stone-500">
        <p>Failed to load analytics.</p>
        <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
      </div>
    );
  }

  const maxColCount = Math.max(...data.column_distribution.map(c => c.count), 1);
  const maxAssigneeCount = data.top_assignees.length > 0 ? Math.max(...data.top_assignees.map(a => a.total), 1) : 1;

  return (
    <div className="h-full overflow-auto" data-testid="board-analytics">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="outline"
            onClick={() => navigate(`/board/${boardId}`)}
            className="rounded-xl"
            data-testid="back-to-board-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={2} />
            Back to Board
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-stone-900">
              {data.board_name} — Analytics
            </h1>
            <p className="text-sm text-stone-500 mt-0.5">Board performance and insights</p>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard icon={BarChart3} label="Total Tickets" value={data.total_tickets} color="text-stone-600 bg-stone-100" />
          <StatCard icon={CheckCircle2} label="Completed" value={data.completed_count} color="text-emerald-600 bg-emerald-50" />
          <StatCard icon={Target} label="Completion Rate" value={`${data.completion_rate}%`} color="text-blue-600 bg-blue-50" />
          <StatCard icon={Clock} label="Avg TTC" value={formatTTC(data.avg_ttc)} color="text-primary bg-primary/10" />
          <StatCard icon={AlertTriangle} label="Overdue" value={data.overdue_count} color="text-rose-600 bg-rose-50" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Column Distribution */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-soft">
            <h2 className="text-sm font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary" strokeWidth={2} />
              Ticket Distribution by Column
            </h2>
            {data.column_distribution.length > 0 ? (
              <div className="space-y-3">
                {data.column_distribution.map((col) => (
                  <div key={col.column_id} data-testid={`col-dist-${col.column_id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-stone-700 font-medium">{col.name}</span>
                      <span className="text-sm font-semibold text-stone-900">{col.count}</span>
                    </div>
                    <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-primary to-amber-400 rounded-full transition-all duration-500"
                        style={{ width: `${(col.count / maxColCount) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-stone-400">No columns found</p>
            )}
          </div>

          {/* Priority Breakdown */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-soft">
            <h2 className="text-sm font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-primary" strokeWidth={2} />
              Priority Breakdown
            </h2>
            <div className="space-y-4">
              <PriorityBar label="High" count={data.priority_counts.high} total={data.total_tickets} color="bg-rose-500" />
              <PriorityBar label="Medium" count={data.priority_counts.medium} total={data.total_tickets} color="bg-amber-400" />
              <PriorityBar label="Low" count={data.priority_counts.low} total={data.total_tickets} color="bg-emerald-400" />
            </div>

            {/* Bottleneck Highlight */}
            {data.bottleneck_column && data.bottleneck_count > 0 && (
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="bottleneck-indicator">
                <p className="text-sm font-semibold text-amber-800">Bottleneck Detected</p>
                <p className="text-xs text-amber-700 mt-1">
                  <span className="font-bold">{data.bottleneck_column}</span> has {data.bottleneck_count} tickets — most among active columns.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Top Assignees */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-soft">
            <h2 className="text-sm font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" strokeWidth={2} />
              Top Assignees
            </h2>
            {data.top_assignees.length > 0 ? (
              <div className="space-y-3">
                {data.top_assignees.map((a, i) => (
                  <div key={`assignee-${a.name}-${i}`} data-testid={`assignee-row-${i}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-stone-700 font-medium">{a.name}</span>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-stone-500">{a.total} total</span>
                        <span className="text-emerald-600 font-medium">{a.completed} done</span>
                      </div>
                    </div>
                    <div className="h-2.5 bg-stone-100 rounded-full overflow-hidden flex">
                      <div
                        className="h-full bg-emerald-400 rounded-l-full transition-all duration-500"
                        style={{ width: `${(a.completed / maxAssigneeCount) * 100}%` }}
                      />
                      <div
                        className="h-full bg-stone-300 transition-all duration-500"
                        style={{ width: `${((a.total - a.completed) / maxAssigneeCount) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-stone-400">No assignees found</p>
            )}
          </div>

          {/* TTC Distribution */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-soft">
            <h2 className="text-sm font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" strokeWidth={2} />
              Time to Complete (per ticket)
            </h2>
            {data.ttc_distribution.length > 0 ? (
              <div className="space-y-2.5">
                {data.ttc_distribution.map((t, i) => {
                  const maxTtc = Math.max(...data.ttc_distribution.map(x => x.ttc_minutes), 1);
                  return (
                    <div key={`ttc-${t.title}-${i}`} className="flex items-center gap-3" data-testid={`ttc-row-${i}`}>
                      <span className="text-xs text-stone-600 truncate w-32 flex-shrink-0" title={t.title}>{t.title}</span>
                      <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-primary to-amber-400 rounded-full"
                          style={{ width: `${(t.ttc_minutes / maxTtc) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-stone-700 w-14 text-right">{formatTTC(t.ttc_minutes)}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-stone-400">No completed tickets with TTC data</p>
            )}
          </div>
        </div>

        {/* TTC per User */}
        {data.ttc_per_user && data.ttc_per_user.length > 0 && (
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-soft mb-8">
            <h2 className="text-sm font-semibold text-stone-900 mb-5 flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" strokeWidth={2} />
              Average Time to Complete per User
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="ttc-per-user-table">
                <thead>
                  <tr className="text-left border-b border-stone-200">
                    <th className="pb-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">User</th>
                    <th className="pb-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Avg TTC</th>
                    <th className="pb-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Fastest</th>
                    <th className="pb-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Slowest</th>
                    <th className="pb-3 text-xs font-semibold text-stone-500 uppercase tracking-wider text-right">Tickets</th>
                    <th className="pb-3 text-xs font-semibold text-stone-500 uppercase tracking-wider w-1/3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.ttc_per_user.map((u, i) => {
                    const maxAvg = Math.max(...data.ttc_per_user.map(x => x.avg_ttc), 1);
                    return (
                      <tr key={`ttc-user-${u.name}-${i}`} className="border-b border-stone-50 last:border-b-0" data-testid={`ttc-user-row-${i}`}>
                        <td className="py-3 text-sm font-medium text-stone-800">{u.name}</td>
                        <td className="py-3 text-sm font-semibold text-stone-900">{formatTTC(u.avg_ttc)}</td>
                        <td className="py-3 text-xs text-emerald-600">{formatTTC(u.min_ttc)}</td>
                        <td className="py-3 text-xs text-rose-500">{formatTTC(u.max_ttc)}</td>
                        <td className="py-3 text-sm text-stone-600 text-right">{u.completed}</td>
                        <td className="py-3 pl-4">
                          <div className="h-2.5 bg-stone-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-primary to-amber-400 rounded-full transition-all duration-500"
                              style={{ width: `${(u.avg_ttc / maxAvg) * 100}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-soft" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-stone-500 uppercase tracking-wider">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" strokeWidth={2} />
        </div>
      </div>
      <p className="text-2xl font-bold text-stone-900">{value}</p>
    </div>
  );
}

function PriorityBar({ label, count, total, color }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div data-testid={`priority-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-stone-700 font-medium">{label}</span>
        <span className="text-sm font-semibold text-stone-900">{count} <span className="text-stone-400 font-normal text-xs">({Math.round(pct)}%)</span></span>
      </div>
      <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default BoardAnalytics;
